import { useState } from "react";
import { Image, ImageProps } from "@caspeco/casper-ui-library.components.image";
import { Skeleton } from "@caspeco/casper-ui-library.components.skeleton";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Text } from "@caspeco/casper-ui-library.components.text";
import { useTranslation } from "react-i18next";

export interface ImageWithSkeletonProps extends ImageProps {
    src?: string;
    alt: string;
    skeletonWidth?: string;
    skeletonHeight?: string;
}

/**
 * A component that shows a skeleton while an image is loading.
 * Once the image is loaded, the skeleton is hidden and the image is shown.
 * If the image fails to load, a fallback message is displayed.
 */
const ImageWithSkeleton = ({
    src,
    alt,
    skeletonWidth,
    skeletonHeight,
    ...restProps
}: ImageWithSkeletonProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const { t } = useTranslation();

    if (!src) {
        return (
            <Box width={restProps.width} height={restProps.height}>
                <Text>{t("imageWithSkeleton.imageFailure")}</Text>
            </Box>
        );
    }

    const handleImageLoad = () => {
        setIsLoaded(true);
    };

    const handleImageError = () => {
        setIsLoaded(true);
        setHasError(true);
    };

    // Use image dimensions for skeleton if no skeleton dimensions provided
    const skWidth = skeletonWidth !== undefined ? skeletonWidth : restProps.width;
    const skHeight = skeletonHeight !== undefined ? skeletonHeight : restProps.height;

    return (
        <Box position="relative">
            {!isLoaded && (
                <Skeleton
                    h={skHeight || "100%"}
                    w={skWidth || "100%"}
                    data-testid="image-skeleton"
                />
            )}

            {hasError ? (
                <Text>{t("imageWithSkeleton.imageFailure")}</Text>
            ) : (
                <Image
                    src={src}
                    alt={alt}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    display={isLoaded ? "block" : "none"}
                    {...restProps}
                />
            )}
        </Box>
    );
};

export default ImageWithSkeleton;
